import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap, startWith } from 'rxjs/operators';
import { OperatorSignService } from 'src/app/services/operator-sign.service';

export type SignServiceStatus = 'online' | 'offline' | 'checking';

@Component({
    selector: 'app-operator-sign-status',
    templateUrl: './operator-sign-status.component.html',
    styleUrls: ['./operator-sign-status.component.scss']
})
export class OperatorSignStatusComponent implements OnInit, OnDestroy {
    @Output() statusChange = new EventEmitter<SignServiceStatus>();

    status: SignServiceStatus = 'checking';
    lastCheckTime: Date | null = null;
    isManualChecking: boolean = false;

    private destroy$ = new Subject<void>();
    private checkInterval = 30000; // 30 secunde

    constructor(
        private operatorSignService: OperatorSignService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // Verificare inițială și apoi la interval
        this.startHealthCheck();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Pornește verificarea periodică a stării serviciului
     */
    private startHealthCheck(): void {
        interval(this.checkInterval).pipe(
            startWith(0), // Verificare imediată la început
            takeUntil(this.destroy$),
            switchMap(() => this.operatorSignService.checkServiceHealth())
        ).subscribe({
            next: (isOnline) => {
                this.updateStatus(isOnline ? 'online' : 'offline');
                this.lastCheckTime = new Date();
                this.cdr.markForCheck();
            },
            error: () => {
                this.updateStatus('offline');
                this.lastCheckTime = new Date();
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Verificare manuală a stării serviciului
     */
    recheckStatus(): void {
        if (this.isManualChecking) return;

        this.isManualChecking = true;
        this.status = 'checking';
        this.cdr.markForCheck();

        this.operatorSignService.checkServiceHealth().subscribe({
            next: (isOnline) => {
                this.updateStatus(isOnline ? 'online' : 'offline');
                this.lastCheckTime = new Date();
                this.isManualChecking = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.updateStatus('offline');
                this.lastCheckTime = new Date();
                this.isManualChecking = false;
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Actualizează status-ul și emite evenimentul
     */
    private updateStatus(newStatus: 'online' | 'offline'): void {
        const previousStatus: SignServiceStatus = this.status;
        // Always update if status changed or if we're coming from 'checking' state
        if (previousStatus !== newStatus || previousStatus === ('checking' as SignServiceStatus)) {
            this.status = newStatus;
            this.statusChange.emit(newStatus);
        }
    }

    /**
     * Returnează textul pentru status
     */
    getStatusText(): string {
        switch (this.status) {
            case 'online':
                return 'Semnătură electronică disponibilă';
            case 'offline':
                return 'Serviciul de semnare nu este activ';
            case 'checking':
                return 'Se verifică...';
            default:
                return '';
        }
    }

    /**
     * Returnează clasa CSS pentru status
     */
    getStatusClass(): string {
        return `status-${this.status}`;
    }
}
